import React, { useState } from 'react';
import { X, AlertCircle, Check, PoundSterling, Clock, Home, Scissors, CreditCard, Calendar, Ruler } from 'lucide-react';

interface PolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  serviceName: string;
}

const PolicyModal: React.FC<PolicyModalProps> = ({ isOpen, onClose, onAccept, serviceName }) => {
  const [hasReadPolicy, setHasReadPolicy] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (acceptedPolicy) {
      onAccept();
    }
  };

  const policyItems = [
    {
      icon: <PoundSterling className="w-5 h-5 text-primary-600" />,
      title: "Non-Refundable Deposit",
      description: "A £20 non-refundable deposit is required to secure your booking"
    },
    {
      icon: <CreditCard className="w-5 h-5 text-primary-600" />,
      title: "Payment Methods",
      description: "Remaining balance is to be paid in cash or by bank transfer on the day of your appointment"
    },
    {
      icon: <Calendar className="w-5 h-5 text-primary-600" />,
      title: "Rescheduling & Cancellation",
      description: "Rescheduling/cancellation must be made 48hrs before appointment"
    },
    {
      icon: <Clock className="w-5 h-5 text-warning-600" />,
      title: "Punctuality Policy",
      description: "Late arrival over 40 minutes might result in cancellation of the appointment with no refund"
    },
    {
      icon: <Scissors className="w-5 h-5 text-primary-600" />,
      title: "Variable Pricing",
      description: "Prices may vary with thicker and longer hair because this might require more hair extension"
    },
    {
      icon: <Home className="w-5 h-5 text-primary-600" />,
      title: "Best Booking Day",
      description: "The best day to book is your off day as braiding time may vary depending on hair density and length"
    },
    {
      icon: <Ruler className="w-5 h-5 text-primary-600" />,
      title: "Hair Length Requirement",
      description: "Hair must be at least 2-3 inches long depending on style"
    },
    {
      icon: <Scissors className="w-5 h-5 text-success-600" />,
      title: "Hair Extensions Included",
      description: "Hair is included in all our services - if you want otherwise kindly notify me before appointment"
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col max-h-[95vh] min-h-[80vh]">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-primary-600 to-primary-700 text-white p-4 sm:p-6 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="pr-12">
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Booking Policy & Terms</h2>
            <p className="text-sm sm:text-base text-primary-100">
              Please review our booking policy for <span className="font-semibold">{serviceName}</span>
            </p>
          </div>
        </div>

        {/* Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-primary-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg font-semibold">Important Information</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              To ensure the best experience for all our clients, please read and understand our booking policy below.
            </p>
          </div>

          <div className="space-y-4 mb-8">
            {policyItems.map((item, index) => (
              <div 
                key={index}
                className="flex items-start p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl transition-all duration-300 hover:shadow-md"
              >
                <div className="flex-shrink-0 mr-4 mt-0.5">
                  {item.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Information */}
          <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-xl mb-6">
            <h4 className="font-semibold text-primary-800 dark:text-primary-200 mb-2">
              What to Expect
            </h4>
            <ul className="text-sm text-primary-700 dark:text-primary-300 space-y-1">
              <li>• Professional consultation before your service begins</li>
              <li>• High-quality hair extensions included in all services</li>
              <li>• Comfortable, clean, and relaxing salon environment</li>
              <li>• Aftercare advice to maintain your new style</li>
              <li>• Flexible timing based on your hair's unique characteristics</li>
            </ul>
          </div>

          {/* Scroll Indicator */}
          <div className="text-center mb-4">
            <button
              onClick={() => setHasReadPolicy(true)}
              className={`text-sm transition-colors ${
                hasReadPolicy 
                  ? 'text-success-600 dark:text-success-400' 
                  : 'text-primary-600 dark:text-primary-400 hover:text-primary-700'
              }`}
            >
              {hasReadPolicy ? (
                <span className="flex items-center justify-center">
                  <Check className="w-4 h-4 mr-1" />
                  Policy reviewed
                </span>
              ) : (
                'Click here to confirm you have read the policy'
              )}
            </button>
          </div>

          {/* Agreement Checkbox */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <label className="flex items-start cursor-pointer group">
              <input
                type="checkbox"
                checked={acceptedPolicy}
                onChange={(e) => setAcceptedPolicy(e.target.checked)}
                disabled={!hasReadPolicy}
                className="mt-1 mr-3 w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 flex-shrink-0"
              />
              <span className={`text-sm transition-colors ${
                !hasReadPolicy 
                  ? 'text-gray-400 dark:text-gray-600' 
                  : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100'
              }`}>
                I have read, understood, and agree to the booking policy and terms outlined above. 
                I understand that a £20 non-refundable deposit is required to secure my booking and that the 
                remaining balance will be paid in cash or by bank transfer on the day of my appointment.
              </span>
            </label>
          </div>
        </div>

        {/* Footer - Always visible */}
        <div className="bg-gray-50 dark:bg-gray-800/50 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={!acceptedPolicy}
              className={`px-4 sm:px-6 py-2 rounded-lg font-medium transition-all duration-200 text-sm sm:text-base ${
                acceptedPolicy
                  ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }`}
            >
              {acceptedPolicy ? (
                <span className="flex items-center">
                  <Check className="w-4 h-4 mr-2" />
                  Continue to Booking
                </span>
              ) : (
                'Accept Policy to Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyModal;